import { useState, useEffect, useCallback, useRef } from 'react'
import {
  subscribeToMyNotes,
  subscribeToAllUserNotes,
  createUserNote,
  updateUserNote,
  softDeleteUserNote
} from '../firebase/userNotes'
import './MyNotes.css'

function previewFromBody(body) {
  const line = (body || '').trim().split('\n')[0] || ''
  return line.slice(0, 80) + (line.length > 80 ? '…' : '')
}

function shortOwnerLabel(uid) {
  if (!uid || typeof uid !== 'string') return ''
  return uid.length > 10 ? `${uid.slice(0, 6)}…${uid.slice(-4)}` : uid
}

const MyNotes = ({ user, isAdmin = false }) => {
  const [notes, setNotes] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedId, setSelectedId] = useState(null)
  const [title, setTitle] = useState('')
  const [body, setBody] = useState('')
  const [deleteConfirm, setDeleteConfirm] = useState({ show: false })
  const [saving, setSaving] = useState(false)
  const saveTimerRef = useRef(null)
  const lastWrittenRef = useRef({ title: '', body: '' })
  const hydratedForSelectionRef = useRef(null)

  useEffect(() => {
    if (!user?.uid) return
    setLoading(true)
    const unsub = isAdmin
      ? subscribeToAllUserNotes((list) => {
          setNotes(list)
          setLoading(false)
        })
      : subscribeToMyNotes(user.uid, (list) => {
          setNotes(list)
          setLoading(false)
        })
    return () => unsub()
  }, [user?.uid, isAdmin])

  const selected = notes.find((n) => n.id === selectedId) || null
  const selectedEditable =
    Boolean(selected && user?.uid) &&
    selected.owner === user.uid &&
    selected.hidden !== true

  useEffect(() => {
    if (!selectedId) {
      hydratedForSelectionRef.current = null
      setTitle('')
      setBody('')
      lastWrittenRef.current = { title: '', body: '' }
      return
    }
    const n = notes.find((x) => x.id === selectedId)
    if (!n) return
    if (hydratedForSelectionRef.current === selectedId) return
    hydratedForSelectionRef.current = selectedId
    const t = n.title ?? ''
    const b = typeof n.body === 'string' ? n.body : ''
    setTitle(t)
    setBody(b)
    lastWrittenRef.current = { title: t, body: b }
  }, [selectedId, notes])

  const flushSave = useCallback(async () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current)
      saveTimerRef.current = null
    }
    if (!selectedId || !user?.uid) return
    const note = notes.find((n) => n.id === selectedId)
    if (!note || note.owner !== user.uid || note.hidden === true) return
    const t = title
    const b = body
    if (
      t === lastWrittenRef.current.title &&
      b === lastWrittenRef.current.body
    ) {
      return
    }
    try {
      setSaving(true)
      await updateUserNote(selectedId, { title: t, body: b })
      lastWrittenRef.current = { title: t, body: b }
    } catch (e) {
      console.error(e)
      alert('Could not save note. Please try again.')
    } finally {
      setSaving(false)
    }
  }, [selectedId, title, body, notes, user?.uid])

  useEffect(() => {
    if (!selectedId || !user?.uid) return
    const note = notes.find((n) => n.id === selectedId)
    if (!note || note.owner !== user.uid || note.hidden === true) return
    if (
      title === lastWrittenRef.current.title &&
      body === lastWrittenRef.current.body
    ) {
      return
    }
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    saveTimerRef.current = setTimeout(() => {
      flushSave()
    }, 750)
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current)
    }
  }, [title, body, selectedId, flushSave, notes, user?.uid])

  const handleSelectNote = async (id) => {
    if (id === selectedId) return
    await flushSave()
    hydratedForSelectionRef.current = null
    setSelectedId(id)
  }

  const handleNewNote = async () => {
    if (!user?.uid) return
    await flushSave()
    try {
      const id = await createUserNote(user.uid, {
        title: 'Untitled note',
        body: ''
      })
      hydratedForSelectionRef.current = null
      setTitle('Untitled note')
      setBody('')
      lastWrittenRef.current = { title: 'Untitled note', body: '' }
      setSelectedId(id)
    } catch (e) {
      console.error(e)
      alert('Could not create note. Please try again.')
    }
  }

  const askDelete = () => {
    if (!selectedId || !selectedEditable) return
    setDeleteConfirm({ show: true })
  }

  const readOnlyReason =
    selected && !selectedEditable
      ? selected.hidden === true
        ? 'Deleted notes are read only.'
        : selected.owner !== user?.uid
          ? "Other users' notes are read only."
          : null
      : null

  const confirmDelete = async () => {
    if (!selectedId || !selectedEditable) return
    const id = selectedId
    try {
      await flushSave()
      if (saveTimerRef.current) {
        clearTimeout(saveTimerRef.current)
        saveTimerRef.current = null
      }
      await softDeleteUserNote(id)
      setDeleteConfirm({ show: false })
      setSelectedId(null)
    } catch (e) {
      console.error(e)
      alert('Could not delete note. Please try again.')
      setDeleteConfirm({ show: false })
    }
  }

  if (!user) return null

  return (
    <div className="my-notes-page">
      <div className="my-notes-layout">
        <aside className="my-notes-sidebar">
          <div className="my-notes-sidebar-header">
            <button type="button" className="my-notes-new-btn" onClick={handleNewNote}>
              + New note
            </button>
          </div>
          <div className="my-notes-list">
            {loading && <p className="my-notes-muted">Loading…</p>}
            {!loading && notes.length === 0 && (
              <p className="my-notes-muted my-notes-empty-hint">
                No notes yet. Create one to get started.
              </p>
            )}
            {!loading &&
              notes.map((n) => {
                const isDeleted = n.hidden === true
                const ownerUid = n.owner
                return (
                  <button
                    key={n.id}
                    type="button"
                    className={`my-notes-list-item ${n.id === selectedId ? 'active' : ''} ${isDeleted ? 'is-deleted' : ''}`}
                    onClick={() => handleSelectNote(n.id)}
                  >
                    <span className="my-notes-list-title-row">
                      <span className="my-notes-list-title">
                        {(n.title || 'Untitled note').trim() || 'Untitled note'}
                      </span>
                      {isDeleted && (
                        <span className="my-notes-badge my-notes-badge-deleted">Deleted</span>
                      )}
                    </span>
                    {isAdmin && ownerUid && (
                      <span className="my-notes-list-owner" title={ownerUid}>
                        <span className="my-notes-owner-prefix">Owner</span>
                        {ownerUid === user.uid ? ' · You' : ` · ${shortOwnerLabel(ownerUid)}`}
                      </span>
                    )}
                    <span className="my-notes-list-preview">
                      {previewFromBody(n.body) || 'Empty note'}
                    </span>
                  </button>
                )
              })}
          </div>
        </aside>

        <section
          className={`my-notes-editor-panel ${selected && !selectedEditable ? 'my-notes-editor-readonly' : ''}`}
        >
          {selected ? (
            <>
              {readOnlyReason && (
                <div className="my-notes-readonly-banner" role="status">
                  {readOnlyReason}
                </div>
              )}
              {isAdmin && selected.owner && (
                <div className="my-notes-owner-bar">
                  <span className="my-notes-owner-bar-label">Belongs to</span>
                  {selected.owner === user.uid ? (
                    <span className="my-notes-owner-bar-value">
                      You
                      <code className="my-notes-owner-uid">{selected.owner}</code>
                    </span>
                  ) : (
                    <span className="my-notes-owner-bar-value" title={selected.owner}>
                      <code className="my-notes-owner-uid">{selected.owner}</code>
                    </span>
                  )}
                </div>
              )}
              <div className="my-notes-editor-toolbar">
                <input
                  className="my-notes-title-input"
                  placeholder="Title"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={500}
                  readOnly={!selectedEditable}
                  aria-label="Note title"
                />
                <div className="my-notes-toolbar-right">
                  {selectedEditable && saving && (
                    <span className="my-notes-save-status">Saving…</span>
                  )}
                  {selectedEditable && (
                    <button type="button" className="my-notes-delete-btn" onClick={askDelete}>
                      Delete
                    </button>
                  )}
                </div>
              </div>
              <textarea
                className="my-notes-body"
                placeholder="Start typing…"
                value={body}
                onChange={(e) => setBody(e.target.value)}
                readOnly={!selectedEditable}
                spellCheck
                aria-label="Note content"
              />
            </>
          ) : (
            <div className="my-notes-placeholder">
              <p>Select a note or create a new one.</p>
            </div>
          )}
        </section>
      </div>

      {deleteConfirm.show && (
        <div className="my-notes-modal-overlay" role="presentation" onClick={() => setDeleteConfirm({ show: false })}>
          <div
            className="my-notes-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="my-notes-delete-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="my-notes-delete-title">Delete this note?</h2>
            <p>This cannot be undone.</p>
            <div className="my-notes-modal-actions">
              <button type="button" className="my-notes-modal-cancel" onClick={() => setDeleteConfirm({ show: false })}>
                Cancel
              </button>
              <button type="button" className="my-notes-modal-delete" onClick={confirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MyNotes
